import React, { useCallback, useEffect, useRef, useState } from "react";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";

import clienteAxios from "../../../config/axios";

/**
 * Mito Alertas — cartel de Despachos.
 * Cada 30 s consulta los avisos a la app que siguen sin confirmar (pasado el
 * tiempo de escalado) y los que el móvil rechazó ("No puedo tomar el servicio").
 * No se cierra a mano: deja de aparecer cuando el móvil confirma o el servicio
 * ya no está asignado a ese móvil (lo filtra el backend).
 * Si Mito Alertas está inactivo el backend devuelve [] y no se muestra nada;
 * si la consulta falla, tampoco: Despachos funciona igual que siempre.
 */

const SEGUNDOS_CONSULTA = 30;

const DESCRIPCION_ESTADO = {
  ENVIADA: { texto: "Sin confirmar", color: "warning" },
  VISTA: { texto: "Visto, sin confirmar", color: "warning" },
  SIN_DISPOSITIVO: { texto: "El móvil no tiene la app registrada", color: "default" },
  ERROR: { texto: "No se pudo enviar el aviso", color: "default" },
  RECHAZADA: { texto: "No puede tomar el servicio", color: "error" },
  CERRADA: { texto: "Sin confirmar", color: "warning" },
};

// fechaEnvio viene con la hora local guardada como UTC (misma convención del sistema)
function haceCuanto(fecha) {
  if (!fecha) return "";
  const ahoraLocal = Date.now() - 3 * 60 * 60 * 1000;
  const segundos = Math.max(0, Math.floor((ahoraLocal - Date.parse(fecha)) / 1000));
  if (segundos < 60) return `hace ${segundos} s`;
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `hace ${minutos} min`;
  return `hace ${Math.floor(minutos / 60)} h ${minutos % 60} min`;
}

function sonar() {
  try {
    const Contexto = window.AudioContext || window.webkitAudioContext;
    if (!Contexto) return;
    const ctx = new Contexto();
    [0, 0.35, 0.7].forEach((inicio) => {
      const osc = ctx.createOscillator();
      const gan = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gan.gain.setValueAtTime(0.25, ctx.currentTime + inicio);
      gan.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + inicio + 0.3);
      osc.connect(gan);
      gan.connect(ctx.destination);
      osc.start(ctx.currentTime + inicio);
      osc.stop(ctx.currentTime + inicio + 0.3);
    });
    setTimeout(() => ctx.close(), 1500);
  } catch (e) {
    // sin sonido si el navegador no lo permite
  }
}

export default function MitoAlertasCartel({ usuario }) {
  const [alertas, setAlertas] = useState([]);
  const [abierto, setAbierto] = useState(false);
  // Reenvío manual por aviso: { [uid]: { enviando, texto, error } }
  const [reenvios, setReenvios] = useState({});
  const montado = useRef(true);

  const consultar = useCallback(async () => {
    try {
      const respuesta = await clienteAxios.post("/api/app/alertas/pendientes", {});
      if (!montado.current) return;
      const lista = Array.isArray(respuesta.data) ? respuesta.data : [];
      setAlertas(lista);
      setReenvios({});
      if (lista.length > 0) {
        setAbierto(true);
        sonar();
      } else {
        setAbierto(false);
      }
    } catch (e) {
      // backend sin Mito Alertas o sin conexión: no se muestra nada
    }
  }, []);

  useEffect(() => {
    montado.current = true;
    consultar();
    const id = setInterval(consultar, SEGUNDOS_CONSULTA * 1000);
    return () => {
      montado.current = false;
      clearInterval(id);
    };
  }, [consultar]);

  const reenviar = async (uid) => {
    setReenvios((r) => ({ ...r, [uid]: { enviando: true } }));
    try {
      await clienteAxios.post("/api/app/alertas/reenviar", { uid, usuario });
      setReenvios((r) => ({
        ...r,
        [uid]: { texto: `Aviso reenviado. Si no confirma, vuelve a aparecer en ${SEGUNDOS_CONSULTA} segundos.` },
      }));
    } catch (e) {
      const data = e && e.response && e.response.data;
      const texto = (data && data.msg) || (typeof data === "string" ? data : "No se pudo reenviar el aviso.");
      setReenvios((r) => ({ ...r, [uid]: { texto, error: true } }));
    }
  };

  if (!abierto || alertas.length === 0) return null;

  return (
    <Dialog open={abierto} maxWidth="sm" fullWidth onClose={() => setAbierto(false)}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "#C62828", color: "#fff" }}>
        <NotificationsActiveOutlinedIcon />
        Mito Alertas — {alertas.length} aviso{alertas.length > 1 ? "s" : ""} para revisar
      </DialogTitle>
      <DialogContent sx={{ pt: "16px !important" }}>
        <Stack spacing={1.5}>
          {alertas.map((a) => {
            const estado = DESCRIPCION_ESTADO[a.estado] || { texto: a.estado, color: "default" };
            const reenvio = reenvios[a.uid] || {};
            return (
              <Paper
                key={a.uid}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderColor: a.estado === "RECHAZADA" ? "error.main" : "warning.main",
                  borderWidth: 2,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <div>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Móvil {a.movil || "-"} · Indicativo {a.servicioId}
                    </Typography>
                    <Typography variant="body2">{a.titulo}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Enviado {haceCuanto(a.fechaEnvio)}
                    </Typography>
                    {a.estado === "RECHAZADA" && a.motivoRechazo ? (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Motivo: {a.motivoRechazo}
                      </Typography>
                    ) : null}
                  </div>
                  <Chip size="small" color={estado.color} label={estado.texto} />
                </Stack>
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                  {reenvio.texto ? (
                    <Typography variant="caption" color={reenvio.error ? "error" : "success.main"}>
                      {reenvio.texto}
                    </Typography>
                  ) : null}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => reenviar(a.uid)}
                    disabled={!!reenvio.enviando || (!!reenvio.texto && !reenvio.error)}
                  >
                    {reenvio.enviando ? "Reenviando..." : "Reenviar aviso"}
                  </Button>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
          Reenviá el aviso, llamá al móvil o reasigná el servicio. El aviso se repite cada{" "}
          {SEGUNDOS_CONSULTA} segundos hasta que el móvil confirme desde la app o el
          servicio se libere o reasigne.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAbierto(false)}>Más tarde</Button>
      </DialogActions>
    </Dialog>
  );
}
